import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { getCloudId } from '@helpcenter/meta';
import { di } from 'react-magnetic-di';
import { useService } from '@atlassian/help-center-common-util/use-service';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';
import type { ApiFormChoicesResponse } from '@atlassian/proforma-form-renderer/form-choices';

import {
	useRequestTypeId,
	useServiceDeskId,
} from '../../controllers/request-create-context-provider';
import { proformaGasAnalyticsFetch } from '../../common/services/proforma-gas-analytics-fetch';
import { PROFORMA_STARGATE_URL_PREFIX } from '../constants';

/**
 * Fetches form choices from the ProForma Stargate backend.
 *
 * Uses serviceDeskId because this calls the ProForma backend API which passes
 * the value to JSM REST APIs that only accept serviceDeskId.
 * portalId and serviceDeskId are NOT interchangeable.
 *
 * See docs/PORTAL_ID_AND_SERVICE_DESK_ID.md for the distinction.
 */
export const useGetFormChoicesService = () => {
	di(useServiceDeskId, useRequestTypeId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();
	const cloudId = getCloudId();
	const serviceDeskId = useServiceDeskId();
	const requestTypeId = useRequestTypeId();

	const request = useCallback(
		() =>
			proformaGasAnalyticsFetch<ApiFormChoicesResponse>(
				`${PROFORMA_STARGATE_URL_PREFIX}/${cloudId}/api/3/portal/${serviceDeskId}/requesttype/${requestTypeId}/formchoices`,
				{ method: 'GET' },
				createAnalyticsEvent,
				errorUtils,
			),
		[cloudId, serviceDeskId, requestTypeId, createAnalyticsEvent, errorUtils],
	);

	return useService(request);
};
